import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import useTripCreation from "@/stores/new-trip/useNewTrip";

interface ConfirmationStepProps {
  onSubmit: () => void;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ onSubmit }) => {
  const { tripName, leavingFrom, leavingDate, isSubmitting } =
    useTripCreation();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white p-8 rounded-3xl shadow-sm space-y-6 mb-12 border border-indigo-100"
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <h2 className="text-2xl font-medium text-indigo-950">
          Review Your Trip
        </h2>
        <p className="text-indigo-600 text-sm font-light">
          Let's make sure everything is correct
        </p>
      </motion.div>

      <motion.div
        className="space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <TripDetail label="Trip Name" value={tripName} />
        <TripDetail label="Departure" value={leavingFrom?.name || ""} />
        <TripDetail
          label="Date"
          value={leavingDate ? format(leavingDate, "PPPP") : "Not set"}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex items-center justify-center"
      >
        <Button
          variant="default"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-6 px-8 rounded-2xl w-full transition-colors duration-200 group"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center space-x-3">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Creating Trip...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center">
              <span>Create Trip</span>
              <ArrowRight className="ml-2 h-5 w-5 transform transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          )}
        </Button>
      </motion.div>
    </motion.div>
  );
};

interface TripDetailProps {
  label: string;
  value: string;
}

const TripDetail: React.FC<TripDetailProps> = ({ label, value }) => (
  <motion.div className="flex flex-col space-y-1 p-4 rounded-2xl border border-indigo-100 bg-indigo-50/30">
    <p className="text-sm text-indigo-600 font-light">{label}</p>
    <p className="text-md text-indigo-950 font-medium">{value}</p>
  </motion.div>
);

export default ConfirmationStep;
